import React, { useMemo, useEffect, useState } from "react";
import {
  Table,
  Flex,
  useDisclosure,
  TableContainer,
  HStack,
  Tooltip,
  IconButton,
  Icon,
  useColorModeValue,
  Select,
  Box,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { RiEdit2Fill } from "react-icons/ri";
import { FaTrash } from "react-icons/fa";
import Card from "components/Card/Card.js";
import { useGetPaginatedContacts, useDeleteContact } from "queries/useContact";
import { useForm } from "react-hook-form";
import useDataTable from "hooks/useDataTable";
import { dynamicSort } from "utils/helpers";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import Header from "components/ui/table/Header";
import Body from "components/ui/table/Body";
import Pagination from "components/ui/table/Pagination";
import { Search } from "components/ui/table/Search";
import { FaPlus } from "react-icons/fa6";
import { CreatedAt } from "components/CellElements";
import { Basic } from "components/CellElements";
import { Team } from "components/CellElements";
import { ContactName } from "components/CellElements";

function MarketingContacts() {
  const {
    formMethods,
    table,
    deleteModal,
    navigate,
    bgButton,
  } = useMarketingContacts();

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <HStack spacing={4} mb={4} mx="6">
          <Search methods={formMethods} />
          <Box>
            <Select
              w="140px"
              placeholder=""
              {...formMethods.register("league")}
              defaultValue={
                localStorage.getItem("marketingContactsLeague") || ""
              }
            >
              <option value="">All Leagues</option>
              <option value="NFL">NFL</option>
              <option value="XFL">XFL</option>
              <option value="CFL">CFL</option>
              <option value="UFL">UFL</option>
              <option value="NFLPA">NFLPA</option>
              <option value="College">College</option>
            </Select>
          </Box>
          <Tooltip label="Add New Marketing Contact" placement="top">
            <IconButton
              aria-label="add new marketing contact"
              icon={<Icon as={FaPlus} boxSize={4} />}
              colorScheme="primary"
              bg={bgButton}
              onClick={() => navigate("/marketing-contacts/create")}
            />
          </Tooltip>
        </HStack>
        <TableContainer>
          <Table {...table.getTableProps()}>
            <Header {...table.header} />
            <Body {...table.body} />
          </Table>
        </TableContainer>
        <Pagination mt="10" {...table.pagination} />
        <DeleteConfirmationModal {...deleteModal} />
      </Card>
    </Flex>
  );
}

export default MarketingContacts;

const useMarketingContacts = () => {
  const navigate = useNavigate();
  const deleteModal = useDisclosure();
  const formMethods = useForm({
    defaultValues: {
      search: localStorage.getItem("marketingContactsSearch") || "",
      league: localStorage.getItem("marketingContactsLeague") || "",
    },
  });

  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("marketingContactsPage");
    // return savedPage ? parseInt(savedPage, 10) + 1 : 1;
    return 1;
  });
  const [limit, setLimit] = useState(() => {
    const savedLimit = localStorage.getItem("marketingContactsLimit");
    return savedLimit ? parseInt(savedLimit, 10) : 50;
  });
  const [currentSort, setCurrentSort] = useState([["createdAt", "DESC"]]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const watchSearch = formMethods?.watch("search");
  const watchLeague = formMethods?.watch("league");

  const getContacts = useGetPaginatedContacts({
    page: currentPage,
    limit,
    search: watchSearch,
    order: currentSort,
    filters: {
      type: "marketing_contact",
      ...(watchLeague ? { league: watchLeague } : {}),
    },
  });
  const deleteContact = useDeleteContact({
    onSuccess: () => deleteModal.onClose(),
  });

  const onSort = dynamicSort(["createdAt"]);
  const data = useMemo(() => getContacts.data?.data?.contacts || [], [
    JSON.stringify(getContacts.data),
  ]);

  const onDelete = (contact) => {
    setSelectedContactId(contact?.id);
    deleteModal.onOpen();
  };

  const onEdit = (contact) =>
    navigate(`/marketing-contacts/view/${contact?.id}`);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        id: "name",
        accessor: (contact) => [
          contact.firstName,
          contact.lastName,
          contact.role,
        ],
        Cell: ({ value: [firstName, lastName, role] }) => (
          <ContactName
            name={`${firstName} ${lastName}`}
            email={role}
            team={null}
          />
        ),
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "Team",
        accessor: "team",
        Cell: ({ value }) => (
          <Team
            logo={value?.logoUrl}
            name={
              value?.league === "NFLPA"
                ? "-"
                : value?.city
                ? `${value?.city} ${value?.name}`
                : "-"
            }
          />
        ),
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "League",
        accessor: (contact) => [contact.team, contact?.team?.league],
        Cell: ({ value: [team, league] }) => (
          <Basic text={league || team?.name} />
        ),
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => <CreatedAt date={value} />,
      },
      {
        accessor: "id",
        disableSortBy: true,
        Cell: ({ row }) => (
          <HStack spacing={3}>
            <Tooltip label="View" placement="top">
              <IconButton
                aria-label="view marketing contact"
                icon={<Icon as={RiEdit2Fill} boxSize={5} />}
                onClick={() => onEdit(row.original)}
              />
            </Tooltip>
            <Tooltip label="Delete" placement="top">
              <IconButton
                aria-label="delete marketing contact"
                icon={<Icon as={FaTrash} boxSize={4} />}
                colorScheme="red"
                onClick={() => onDelete(row.original)}
              />
            </Tooltip>
          </HStack>
        ),
      },
    ],
    []
  );

  const sortTable = useMemo(() => {
    return currentSort.map(([column, direction]) => {
      let id = column?.replace("name", "firstName") || "createdAt";
      let desc = direction === "ASC" ? false : true;
      return { id, desc };
    });
  }, [currentSort]);

  const pageCount = useMemo(() => getContacts.data?.data?.totalPages || 0, [
    getContacts.data,
  ]);

  const table = useDataTable({
    desktopHiddenColumns: [],
    mobileHiddenColumns: [],
    data,
    columns,
    initialState: {
      pageCount,
      pageIndex: currentPage - 1,
      pageSize: limit,
      sortBy: sortTable,
    },
    setter: { setCurrentPage, setLimit },
    isLoading: getContacts.isLoading,
  });

  useEffect(() => {
    getContacts.refetch({
      page: table.pagination.pageIndex,
      limit: table.pagination.pageSize,
      order: currentSort,
      search: watchSearch,
    });

    localStorage.setItem(
      "marketingContactsPage",
      table.pagination.pageIndex.toString()
    );
    localStorage.setItem(
      "marketingContactsLimit",
      table.pagination.pageSize.toString()
    );

    if (watchSearch !== undefined) {
      localStorage.setItem("marketingContactsSearch", watchSearch || "");
    }

    if (watchLeague !== undefined) {
      console.log("watchLeague", watchLeague);
      localStorage.setItem("marketingContactsLeague", watchLeague || "");
    }
  }, [
    table.pagination.pageIndex,
    table.pagination.pageSize,
    watchSearch,
    watchLeague,
    JSON.stringify(table.sort?.sortBy),
  ]);

  useEffect(() => {
    let sort = table.sort?.sortBy?.map((s) => {
      let id = s.id?.replace("name", "firstName") || "createdAt";
      let desc = s.desc == false ? "ASC" : "DESC";
      return [id, desc];
    });

    setCurrentSort(sort);
  }, [JSON.stringify(table.sort?.sortBy)]);

  const bgButton = useColorModeValue(
    "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
    "gray.800"
  );

  return {
    bgButton,
    formMethods,
    table,
    navigate,
    deleteModal: {
      ...deleteModal,
      onConfirmDelete: () => deleteContact.mutate(selectedContactId),
      isLoading: deleteContact.isLoading,
      title: "Are you sure you want to delete this contact?",
    },
  };
};
